<template>
  <div>
    <v-dialog
      v-model="dialog"
      :max-width="options.width"
      :style="{ zIndex: options.zIndex }"
      @keydown.esc="cancel"
    >
      <v-card>
        <v-container class="px-10 pt-8">
          <p class="subtitle-1 font-weight-bold mb-2">
            Create Security Deposit
          </p>

          <v-form v-model="isFormValid">
            <v-row>
              <v-col class="py-0" cols="12" sm="12" md="12">
                <v-select
                  v-model="unit"
                  dense
                  outlined
                  placeholder="Select Unit Here"
                  :items="
                    units.filter(
                      (u) =>
                        u.value.tenantName
                          .toLowerCase()
                          .includes(searchTenantsList.toLowerCase()) ||
                        u.value.bedName
                          .toLowerCase()
                          .includes(searchTenantsList.toLowerCase())
                    )
                  "
                  :rules="unitRules"
                  :disabled="!units.length"
                >
                  <template v-slot:prepend-item>
                    <v-list-item ripple>
                      <v-list-item-content>
                        <v-text-field
                          v-model="searchTenantsList"
                          outlined
                          dense
                          hide-details
                          placeholder="Search Tenant or Unit"
                        ></v-text-field>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider> </template
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="py-0" cols="12" sm="12" md="12">
                <v-currency-field
                  v-model="amount"
                  type="number"
                  label="Amount"
                  prepend-inner-icon="mdi-cash"
                  dense
                  outlined
                >
                </v-currency-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="py-0" cols="12" sm="12" md="12">
                <v-textarea
                  v-model="description"
                  name="description"
                  label="Description"
                  prepend-inner-icon="mdi-information"
                  dense
                  outlined
                >
                </v-textarea>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="py-0" cols="12" sm="12" md="6">
                <v-menu
                  v-model="refundDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="refundDate"
                      label="Refund Date"
                      v-bind="attrs"
                      readonly
                      dense
                      outlined
                      v-on="on"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon small class="mt-1">mdi-calendar-month</v-icon>
                      </template>
                    </v-text-field>
                  </template>
                  <v-date-picker
                    v-model="refundDate"
                    no-title
                    scrollable
                    @input="refundDateMenu = false"
                  >
                  </v-date-picker>
                </v-menu>
              </v-col>
              <v-col class="py-0" cols="12" sm="12" md="6">
                <v-menu
                  v-model="paymentDueDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="paymentDueDate"
                      label="Payment Due Date"
                      v-bind="attrs"
                      readonly
                      dense
                      outlined
                      v-on="on"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon small class="mt-1">mdi-calendar-month</v-icon>
                      </template>
                    </v-text-field>
                  </template>
                  <v-date-picker
                    v-model="paymentDueDate"
                    no-title
                    scrollable
                    @input="paymentDueDateMenu = false"
                  >
                  </v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-form>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="font-weight-bold"
              color="primary darken-1"
              text
              :disabled="!isFormValid || isNaN(unit.leaseId)"
              @click="finish"
              >Finish</v-btn
            >
            <v-btn
              class="font-weight-bold"
              color="grey darken-1"
              text
              @click="cancel"
              >Cancel</v-btn
            >
          </v-card-actions>
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { DATEPICKER, YYYYMMDD, dayjs } from '../../util/date'

export default {
  data() {
    return {
      dialog: false,
      options: {
        color: 'primary',
        width: 500,
        zIndex: 200
      },
      tenantName: '',
      amount: 0,
      description: '',
      refundDate: '',
      paymentDueDate: '',
      refundDateMenu: false,
      paymentDueDateMenu: false,
      unitName: '',
      tenantsList: [],
      searchTenantsList: '',
      unit: {},
      unitRules: [(v) => !!v || 'Please select a unit'],
      isFormValid: true
    }
  },
  computed: {
    units() {
      const units = this.$store.getters['units/getUnitsFlat']

      return units
        .filter((unit) => unit.status === 'occupied')
        .map((unit) => ({
          text: `${unit.bedName} - ${unit.tenantName}`,
          value: unit
        }))
    }
  },
  methods: {
    open(bedId) {
      this.dialog = true
      if (bedId) {
        const unit = this.units.find((unit) => unit.value.bedId === bedId)
        this.unit = unit && unit.value
        // set the default refund date
        this.refundDate = dayjs(this.unit.moveOutDate).format(DATEPICKER)
      }
    },
    async finish() {
      try {
        this.$root.$setLoading(true)
        await this.$store.dispatch('securityDeposits/create', {
          leaseId: this.unit.leaseId,
          description: this.description,
          paymentDueDate: this.paymentDueDate
            ? dayjs(this.paymentDueDate).format(YYYYMMDD)
            : undefined,
          refundDate: this.refundDate
            ? dayjs(this.refundDate).format(YYYYMMDD)
            : undefined,
          amount: this.amount,
          tenantId: this.unit.tenantId
        })

        this.dialog = false
        this.$root.$setLoading(false)
        await this.$root.$confirm({
          title: 'Create Security Deposit',
          message: `The security deposit has been created.`,
          reconfirmOnly: true,
          confirmText: 'OK',
          options: {
            color: 'success'
          }
        })
        this.$emit('reload')
      } catch (err) {
        await this.$root.$confirm({
          title: 'Create Security Deposit',
          message: err.message,
          reconfirmOnly: true,
          confirmText: 'OK',
          options: {
            color: 'error'
          }
        })
      }
    },
    cancel() {
      this.dialog = false
    }
  }
}
</script>
